/**
 * Icon Abstraction Layer with Theme Support
 *
 * This module centralizes all icon exports and supports theme-specific icons.
 *
 * Usage:
 *   // Direct import (non-themed, for UI icons like chevrons)
 *   import { ChevronDown } from '@/design-system/icons';
 *
 *   // Themed icon component
 *   import { ThemedIcon } from '@/design-system/icons';
 *   <ThemedIcon name="dashboard" className="size-4" />
 *
 *   // Hook for dynamic icon access
 *   import { useThemedIcon } from '@/design-system/icons';
 *   const DashboardIcon = useThemedIcon('dashboard');
 */

import { useTheme } from '@/app/providers';
import type { Theme } from '@/design-system/themes';
import {
    Activity,
    AlertCircle,
    Bell,
    Building2,
    Check,
    CheckCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Cog,
    Download,
    Edit,
    Eye,
    EyeOff,
    Factory,
    FileText,
    Folder,
    Hammer,
    HelpCircle,
    Home,
    Info,
    Languages,
    LayoutDashboard,
    Loader2,
    LogOut,
    MapPin,
    Menu,
    Minus,
    Moon,
    MoreHorizontal,
    Plus,
    Search,
    Settings,
    Sun,
    Trash2,
    Upload,
    User,
    UserCircle,
    Wrench,
    X,
    XCircle,
    type LucideIcon,
} from 'lucide-react';


export {
    Activity, AlertCircle, Bell, Check, CheckCircle, ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp, Cog, Download, Edit, Eye,
    EyeOff, Factory, FileText, Folder, Hammer, HelpCircle, Home, Info, Loader2, LogOut, MapPin, Menu, Minus, Moon, Plus, Search, Sun, Trash2, Upload, User, UserCircle, X, XCircle
};


    import {
        RiBuildingLine,
        RiContactsLine,
        RiMapPin5Line,
    } from '@remixicon/react';
    import { IconComponent, IconName, IconProps, IconTypeMap } from './types';


export type { LucideIcon };


/**
 * Theme-specific icon mappings
 * Add different icons for each theme here
 */
const iconSets: Record<Theme, IconTypeMap> = {
    eurodima: {
        dashboard: LayoutDashboard,
        wrench: Wrench,
        building: Building2,
        user: User,
        settings: Settings,
        company: RiBuildingLine,
        companyAddress: RiMapPin5Line,
        contact: RiContactsLine,
        languages: Languages,
        documents: FileText,
        file: FileText,
        folder: Folder,
        download: Download,
        upload: Upload,
        eye: Eye,
        plus: Plus,
        more: MoreHorizontal,
    },
    tyrolit: {
        dashboard: Home,
        wrench: Hammer,
        building: Factory,
        user: UserCircle,
        settings: Cog,
        company: Building2,
        companyAddress: MapPin,
        contact: User,
        languages: Languages,
        documents: FileText,
        file: FileText,
        folder: Folder,
        download: Download,
        upload: Upload,
        eye: Eye,
        plus: Plus,
        more: MoreHorizontal,
    },
};


/**
 * Hook to get a themed icon component
 * @param name - The icon name from the icon set
 * @returns The icon component for the current theme
 */
export function useThemedIcon(name: IconName): IconComponent {
    const { theme } = useTheme();
    return iconSets[theme][name];
}


interface ThemedIconProps extends IconProps {
    name: IconName;
}

/**
 * Themed icon component that automatically uses the correct icon for the current theme
 */
export function ThemedIcon({ name, ...props }: ThemedIconProps) {
    const Icon = useThemedIcon(name);

    if (!Icon) return <></>

    return <Icon {...props} />;
}

// Legacy exports for backward compatibility (uses eurodima icons)
export {
    Building2, LayoutDashboard, Settings, Wrench
};


